// References (docs/app.md → References). One job: maintain the master records
// that drive extraction and matching — vendors, companies (with their uploader
// email domains), the chart of accounts, vendor default accounts, and the fleet.
// Gated to the References role; a fleet maintainer sees the Vehicles manager
// alone. The API enforces access; this is UX.
//
// Five managers, not six: the provider-key roster belonged to the claims
// workspace, which is out of scope, and is deleted with it.
//
// Two rules run through every manager here:
//   * Retire and reactivate are ONE write — a PATCH of `is_active` carrying the
//     row's version. There are no separate deactivate/reactivate verbs.
//   * Uniqueness is a database constraint, not a pre-flight check. The old
//     screen asked "does this already exist?" before every create, which both
//     cost a round trip and raced; now a violation comes back as
//     `duplicate_key` and its message is shown as written.
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Combobox,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Option,
  SearchBox,
  Spinner,
  Switch,
  Tab,
  TabList,
  Toast,
  ToastTitle,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import {
  AddRegular,
  ArrowUploadRegular,
  EditRegular,
  ArchiveRegular,
  ArrowUndoRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DismissRegular,
} from '@fluentui/react-icons'
import { errorMessage, isApiError } from '../api/errors'
import {
  FLEET_FIELDS,
  FLEET_FIELD_LABEL,
  type FleetField,
  type VehicleImportPreview,
} from '../api/types'
import type {
  Account,
  Company,
  CompanyEmailDomain,
  CompanyScanner,
  Vehicle,
  Vendor,
  VendorCodingRule,
} from '../lib/types'
import { SCREEN_SUBHEADER, TOASTER_ID } from '../lib/constants'
import {
  commitVehicleImport,
  createAccount,
  createCompany,
  createCompanyEmailDomain,
  createCompanyScanner,
  createIntakeSender,
  createVehicle,
  createVendor,
  createVendorCodingRule,
  loadAccountsForManagement,
  loadCompaniesForManagement,
  loadCompanyEmailDomains,
  loadCompanyScanners,
  loadCostCenterReach,
  loadIntakeSenders,
  loadVehiclesForManagement,
  loadVendorCodingRules,
  loadCostCenterLabels,
  loadExpenseTypeLabels,
  loadVendorNames,
  searchCostCenters,
  searchExpenseTypes,
  loadVendorsForManagement,
  normalizeDomain,
  normalizeScannerName,
  previewVehicleImport,
  searchVendors,
  setVendorActive,
  updateAccount,
  updateCompany,
  updateCompanyEmailDomain,
  updateCompanyScanner,
  updateIntakeSender,
  updateVehicle,
  updateVendor,
  updateVendorCodingRule,
} from '../lib/data'
import { entryIsActive, mergeSenderEntries, routedCompanyName, type SenderEntry } from '../lib/senders'
import { ScreenTitle } from '../components/ScreenTitle'
import { ErrorBanner } from '../components/States'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'
import { popupPositionProps, usePopupListboxClass } from '../components/popupPositioning'

const useStyles = makeStyles({
  // Title + subheader on one baseline, as every pipeline screen does it.
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  headerSub: { color: tokens.colorNeutralForeground3 },
  dialogField: { marginBottom: '14px' },
  // The picker + its clear button, on the Add default dialog's two halves.
  pickerRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  // The two outline badges on a Senders row (intake allowed/not, routes-to/none).
  senderBadges: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  // Inline, non-blocking dialog warning (mirrors States.tsx warningBanner).
  // `display: 'block'` is load-bearing, not decoration: Fluent's `<Text>` root
  // (what `Body1` renders) is a `<span>` with `display: inline`, and a
  // multi-line inline box paints its background/border/padding PER LINE BOX
  // (box-decoration-break: slice) rather than around the whole block — the
  // mangled, strikethrough-looking warning the owner screenshotted. Setting it
  // here, on the class every `dialogWarn` caller shares, means no caller has to
  // remember the `block` prop (one of ten call sites here used to; the other
  // nine didn't, which is why only one rendered correctly).
  dialogWarn: {
    display: 'block',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 12px',
    marginBottom: '14px',
  },
  subnav: { marginBottom: '16px' },
  // `display: 'block'` for the same reason `dialogWarn` needs it: `Body1`
  // renders a `<span>` (`display: inline` by default), so `marginBottom` was a
  // no-op and multiple consecutive `mdIntro` paragraphs ran together with no
  // gap or even a word-break between them — found on the new three-paragraph
  // Senders intro, which is the only place this class had ever been used more
  // than once in a row.
  mdIntro: { display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '12px' },
  mdToolbar: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' },
  searchBox: { minWidth: '280px' },
  spacer: { flex: 1 },
  mdHint: { color: tokens.colorNeutralForeground3, padding: '8px 2px' },
  mdList: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  mdRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  mdRowMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  mdRowTitle: { fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Opt-in only (vendor coding rules): the title there is a full sentence, not a
  // short name, and truncating it hides the expense type / cost center it exists
  // to show. Every other master's row stays single-line via `mdRowTitle` alone.
  mdRowTitleWrap: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', wordBreak: 'break-word' },
  mdRowSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mdRowActions: { display: 'flex', gap: '4px', alignItems: 'center' },
  mdRetired: { opacity: 0.55 },
  retireList: { margin: '8px 0', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '2px' },
  // ---- Company email domains (edited on the company, as a list) ----
  domainRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' },
  domainName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
  domainAdd: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  // ---- Fleet spreadsheet import ----
  importSurface: { maxWidth: '760px' },
  importIntro: { display: 'flex', flexDirection: 'column', gap: '12px' },
  importCounts: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  importSectionHead: { fontWeight: tokens.fontWeightSemibold, display: 'block', marginTop: '4px' },
  importDiff: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '30vh',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 10px',
  },
  importDiffRow: { display: 'grid', gridTemplateColumns: '70px minmax(0, 1fr)', gap: '10px', alignItems: 'baseline' },
  importChange: { display: 'block', color: tokens.colorNeutralForeground2 },
  // Two columns: nine fields in one stack is a scroll, and these pair naturally
  // (number/year, VIN/plate, make/model, company/department).
  vehGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: '12px' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '8px 2px' },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '24px 16px',
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground3,
  },
  dropZoneActive: {
    // The full `border` shorthand, not `borderColor` — griffel rejects the
    // four-sided sub-shorthand (it only accepts `border` or the per-side longhands).
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
})

/** A uniqueness violation is the database's answer, and the API's message names
    what collided — show it as written. Anything else gets the context of what we
    were trying to do. */
function writeError(e: unknown, context: string): string {
  if (isApiError(e) && e.code === 'duplicate_key') return e.message
  return `${context}: ${errorMessage(e)}`
}

export function TrainingScreen({ fleetOnly = false }: { fleetOnly?: boolean }) {
  const s = useStyles()
  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>{fleetOnly ? 'Fleet' : 'References'}</ScreenTitle>
        <Body1 className={s.headerSub}>
          {fleetOnly
            ? 'Add, edit, or retire the vehicles that invoice lines can be tagged against.'
            : SCREEN_SUBHEADER.Training}
        </Body1>
      </div>
      <MasterData fleetOnly={fleetOnly} />
    </div>
  )
}

/** Toast helper shared by the managers. */
function useToast() {
  const { dispatchToast } = useToastController(TOASTER_ID)
  return useCallback(
    (title: string, intent: 'success' | 'error' = 'success') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
        </Toast>,
        { intent },
      ),
    [dispatchToast],
  )
}

/** A term that settles before it is used for a server search. */
function useDebounced(value: string, ms = 300): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return settled
}

/** One row in a master list: key fields + Active/Retired badge + actions. */
function MasterRow({
  title,
  subtitle,
  retired,
  busy,
  onEdit,
  editIcon,
  editLabel,
  onToggleActive,
  wrapTitle,
}: {
  title: string
  subtitle?: string
  retired: boolean
  busy: boolean
  onEdit?: () => void
  editIcon?: ReactElement
  editLabel?: string
  onToggleActive: () => void
  /** Lets the title wrap instead of ellipsizing — set only where the title is a
      full sentence rather than a short name (vendor coding rules). */
  wrapTitle?: boolean
}) {
  const s = useStyles()
  return (
    <div className={mergeClasses(s.mdRow, retired && s.mdRetired)}>
      <div className={s.mdRowMain}>
        {/* The native tooltip is for the ellipsized case — where the row hides
            part of its own title. A wrapping title shows all of itself, so a
            tooltip repeating it would just cover the row underneath. */}
        <span
          className={mergeClasses(s.mdRowTitle, wrapTitle && s.mdRowTitleWrap)}
          title={wrapTitle ? undefined : title}
        >
          {title}
        </span>
        {subtitle && <span className={s.mdRowSub}>{subtitle}</span>}
      </div>
      <Badge appearance="outline" color={retired ? 'danger' : 'success'}>
        {retired ? 'Retired' : 'Active'}
      </Badge>
      <div className={s.mdRowActions}>
        {onEdit && (
          <Button
            appearance="subtle"
            size="small"
            icon={editIcon ?? <EditRegular />}
            aria-label={editLabel ?? 'Edit'}
            disabled={busy}
            onClick={onEdit}
          />
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={retired ? <ArrowUndoRegular /> : <ArchiveRegular />}
          disabled={busy}
          onClick={onToggleActive}
        >
          {retired ? 'Reactivate' : 'Retire'}
        </Button>
        {busy && <Spinner size="tiny" />}
      </div>
    </div>
  )
}

/** Generic confirm dialog for retire/reactivate. */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onCancel()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Body1>{body}</Body1>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm} disabled={busy}>
              {confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

type EntityTab = 'vendors' | 'companies' | 'accounts' | 'defaults' | 'scanners' | 'senders' | 'vehicles'

function MasterData({ fleetOnly = false }: { fleetOnly?: boolean }) {
  const s = useStyles()
  const [tab, setTab] = useState<EntityTab>(fleetOnly ? 'vehicles' : 'vendors')
  // A fleet maintainer has authority over vehicles and nothing else, so the
  // other masters are hidden rather than shown-and-broken.
  if (fleetOnly) {
    return (
      <div>
        <Body1 className={s.mdIntro}>
          Retiring is reversible — a retired vehicle drops out of tagging and automatic resolution but stays for audit.
        </Body1>
        <VehiclesManager />
      </div>
    )
  }
  return (
    <div>
      <TabList
        appearance="subtle"
        size="small"
        className={s.subnav}
        selectedValue={tab}
        onTabSelect={(_, d) => setTab(d.value as EntityTab)}
        aria-label="Master data tables"
      >
        <Tab value="vendors">Vendors</Tab>
        <Tab value="companies">Companies</Tab>
        <Tab value="accounts">Accounts</Tab>
        <Tab value="defaults">Defaults</Tab>
        <Tab value="scanners">Scanners</Tab>
        <Tab value="senders">Senders</Tab>
        <Tab value="vehicles">Vehicles</Tab>
      </TabList>
      {tab === 'vendors' && <VendorsManager />}
      {tab === 'companies' && <CompaniesManager />}
      {tab === 'accounts' && <AccountsManager />}
      {tab === 'defaults' && <VendorDefaultsManager />}
      {tab === 'scanners' && <ScannersManager />}
      {tab === 'senders' && <SendersManager />}
      {tab === 'vehicles' && <VehiclesManager />}
    </div>
  )
}

// ---- Vendors ---------------------------------------------------------------
/** Rows painted per page. The master is ~2,700 rows. */
const VENDOR_PAGE = 50
/** How many the server hands back per search. Deep enough that the unfiltered
    list is useful immediately, shallow enough not to ship the whole master. */
const VENDOR_FETCH = 200

// The list is never blank: an empty search loads the first page of the master
// and paints it. Typing searches the WHOLE master server-side (name, code or
// city) rather than filtering the rows already on screen — with 2,700 vendors,
// local filtering would quietly miss most matches.
function VendorsManager() {
  const s = useStyles()
  const toast = useToast()
  const [term, setTerm] = useState('')
  const settledTerm = useDebounced(term)
  const [showRetired, setShowRetired] = useState(false)
  const [rows, setRows] = useState<Vendor[] | null>(null)
  const [page, setPage] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<Vendor | 'new' | null>(null)
  const [confirm, setConfirm] = useState<Vendor | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await loadVendorsForManagement(settledTerm.trim() || undefined, VENDOR_FETCH))
    } catch (e) {
      setError(errorMessage(e))
      setRows([])
    }
  }, [settledTerm])
  useEffect(() => {
    void load()
  }, [load])

  const matched = useMemo(() => (rows ?? []).filter((v) => showRetired || v.is_active), [rows, showRetired])

  // A filter change must not leave you stranded on a page that no longer exists.
  useEffect(() => {
    setPage(0)
  }, [settledTerm, showRetired])

  const pageCount = Math.max(1, Math.ceil(matched.length / VENDOR_PAGE))
  const current = Math.min(page, pageCount - 1)
  const shown = matched.slice(current * VENDOR_PAGE, current * VENDOR_PAGE + VENDOR_PAGE)

  async function toggleActive(v: Vendor) {
    setBusyId(v.id)
    setError(null)
    try {
      await setVendorActive(v.id, !v.is_active, v.row_version)
      toast(v.is_active ? 'Vendor retired' : 'Vendor reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update vendor'))
    } finally {
      setBusyId(null)
    }
  }

  if (rows === null) return <Spinner label="Loading vendors…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search name, code or city…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add vendor
        </Button>
      </div>
      {matched.length === 0 ? (
        <Body1 className={s.mdHint}>No vendors match.</Body1>
      ) : (
        <>
          <div className={s.mdList}>
            {shown.map((v) => (
              <MasterRow
                key={v.id}
                title={v.vendor_name || '(unnamed)'}
                subtitle={[v.vendor_id, [v.city, v.state_or_province].filter(Boolean).join(', ')]
                  .filter(Boolean)
                  .join('  ·  ')}
                retired={!v.is_active}
                busy={busyId === v.id}
                onEdit={() => setEdit(v)}
                onToggleActive={() => (v.is_active ? setConfirm(v) : void toggleActive(v))}
              />
            ))}
          </div>
          <div className={s.pager}>
            <Button
              size="small"
              appearance="subtle"
              icon={<ChevronLeftRegular />}
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              Previous
            </Button>
            <Caption1 className={s.mdHint}>
              {current * VENDOR_PAGE + 1}–{current * VENDOR_PAGE + shown.length} of {matched.length}
              {matched.length >= VENDOR_FETCH ? ' matched so far — narrow the search for more' : ''}
            </Caption1>
            <Button
              size="small"
              appearance="subtle"
              icon={<ChevronRightRegular />}
              iconPosition="after"
              disabled={current >= pageCount - 1}
              onClick={() => setPage(current + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
      <VendorDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Vendor added' : 'Vendor updated')
          void load()
        }}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire vendor?"
        body={`Retire ${confirm?.vendor_name ?? ''}? It stops matching new documents but stays for audit. You can reactivate it later.`}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

/** The editable shape behind VendorDialog — the vendor master's contact
    columns, which the matching pass uses as disambiguation hints. */
interface VendorFields {
  vendorId: string
  vendorName: string
  address1: string
  address2: string
  address3: string
  city: string
  state: string
  zip: string
  phone: string
  fax: string
}
const BLANK_VENDOR: VendorFields = {
  vendorId: '', vendorName: '', address1: '', address2: '', address3: '',
  city: '', state: '', zip: '', phone: '', fax: '',
}

function VendorDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: Vendor | 'new' | null
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [f, setF] = useState<VendorFields>(BLANK_VENDOR)
  const [saving, setSaving] = useState(false)

  // Seed on open (references only `row`, so no exhaustive-deps escape needed).
  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setF(BLANK_VENDOR)
      return
    }
    setF({
      vendorId: row.vendor_id ?? '',
      vendorName: row.vendor_name ?? '',
      address1: row.address_1 ?? '',
      address2: row.address_2 ?? '',
      address3: row.address_3 ?? '',
      city: row.city ?? '',
      state: row.state_or_province ?? '',
      zip: row.postal_code ?? '',
      phone: row.phone ?? '',
      fax: row.fax ?? '',
    })
  }, [row])

  const set = (k: keyof VendorFields) => (_: unknown, d: { value: string }) => setF((p) => ({ ...p, [k]: d.value }))
  const ready = f.vendorId.trim() !== '' && f.vendorName.trim() !== ''

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        await createVendor({
          vendor_id: f.vendorId.trim(),
          vendor_name: f.vendorName.trim(),
          address_1: f.address1.trim() || null,
          address_2: f.address2.trim() || null,
          address_3: f.address3.trim() || null,
          city: f.city.trim() || null,
          state_or_province: f.state.trim() || null,
          postal_code: f.zip.trim() || null,
          phone: f.phone.trim() || null,
          fax: f.fax.trim() || null,
        })
      } else if (existing) {
        await updateVendor(existing.id, {
          row_version: existing.row_version,
          vendor_name: f.vendorName.trim(),
          // Edit sends the current text for every contact field (blank clears it).
          address_1: f.address1.trim() || null,
          address_2: f.address2.trim() || null,
          address_3: f.address3.trim() || null,
          city: f.city.trim() || null,
          state_or_province: f.state.trim() || null,
          postal_code: f.zip.trim() || null,
          phone: f.phone.trim() || null,
          fax: f.fax.trim() || null,
        })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save vendor'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add vendor' : 'Edit vendor'}</DialogTitle>
          <DialogContent>
            <Field label="Vendor ID" required className={s.dialogField}>
              <Input value={f.vendorId} onChange={set('vendorId')} readOnly={!isNew} appearance={isNew ? undefined : 'filled-darker'} />
            </Field>
            <Field label="Vendor name" required className={s.dialogField}>
              <Input value={f.vendorName} onChange={set('vendorName')} />
            </Field>
            <Field label="Address line 1" className={s.dialogField}>
              <Input value={f.address1} onChange={set('address1')} />
            </Field>
            <Field label="Address line 2" className={s.dialogField}>
              <Input value={f.address2} onChange={set('address2')} />
            </Field>
            <Field label="Address line 3" className={s.dialogField}>
              <Input value={f.address3} onChange={set('address3')} />
            </Field>
            <Field label="City" className={s.dialogField}>
              <Input value={f.city} onChange={set('city')} />
            </Field>
            <Field label="State / province" className={s.dialogField}>
              <Input value={f.state} onChange={set('state')} />
            </Field>
            <Field label="ZIP / postal code" className={s.dialogField}>
              <Input value={f.zip} onChange={set('zip')} />
            </Field>
            <Field label="Phone" className={s.dialogField}>
              <Input value={f.phone} onChange={set('phone')} />
            </Field>
            <Field label="Fax" className={s.dialogField}>
              <Input value={f.fax} onChange={set('fax')} />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add vendor' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Companies -------------------------------------------------------------
// RENAMING IS ALLOWED (a deliberate change, not an inherited restriction). In
// the old system record ownership was resolved by team NAME, so a rename handed
// every newly released record to the catch-all team and blinded the company's
// own director. Authority and visibility key on the company ID here, so the
// hazard is gone.
//
// Uploader email domains are edited on the company as a list — a company may
// legitimately have several (it is how an uploader's address files an invoice),
// but a domain may point at only one company, which is the uniqueness the
// matching pass depends on.
function CompaniesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Company[] | null>(null)
  const [domains, setDomains] = useState<CompanyEmailDomain[]>([])
  const [showRetired, setShowRetired] = useState(false)
  const [term, setTerm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [dialogRow, setDialogRow] = useState<Company | 'new' | null>(null)
  const [confirm, setConfirm] = useState<Company | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [cs, ds] = await Promise.all([loadCompaniesForManagement(), loadCompanyEmailDomains()])
      setAll(cs)
      setDomains(ds)
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  /** A company's active domains, by company id. */
  const domainsFor = useCallback(
    (companyId: string) => domains.filter((d) => d.is_active && d.company_id === companyId),
    [domains],
  )

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (all ?? [])
      .filter((c) => showRetired || c.is_active)
      .filter(
        (c) =>
          !t ||
          c.company_name.toLowerCase().includes(t) ||
          c.company_id.toLowerCase().includes(t) ||
          domainsFor(c.company_id).some((d) => d.domain.toLowerCase().includes(t)),
      )
  }, [all, showRetired, term, domainsFor])

  async function toggleActive(c: Company) {
    setBusyId(c.id)
    setError(null)
    try {
      await updateCompany(c.id, { is_active: !c.is_active, row_version: c.row_version })
      toast(c.is_active ? 'Company retired' : 'Company reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update company'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading companies…" />

  const dialogCompany = dialogRow && dialogRow !== 'new' ? dialogRow : null

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search name, ID or email domain…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setDialogRow('new')}>
          Add company
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No companies to show.' : 'No companies match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((c) => (
            <MasterRow
              key={c.id}
              title={c.company_name || '(unnamed)'}
              subtitle={[c.company_id, domainsFor(c.company_id).map((d) => d.domain).join(', ')]
                .filter(Boolean)
                .join('  ·  ')}
              retired={!c.is_active}
              busy={busyId === c.id}
              onEdit={() => setDialogRow(c)}
              onToggleActive={() => (c.is_active ? setConfirm(c) : void toggleActive(c))}
            />
          ))}
        </div>
      )}
      <CompanyDialog
        row={dialogRow}
        domains={dialogCompany ? domainsFor(dialogCompany.company_id) : []}
        onClose={() => setDialogRow(null)}
        onSaved={(msg) => {
          setDialogRow(null)
          toast(msg)
          void load()
        }}
        onChanged={() => void load()}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire company?"
        body={`Retire ${confirm?.company_name ?? ''}? It drops out of the company pickers and out of matching. You can reactivate it later.`}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function CompanyDialog({
  row,
  domains,
  onClose,
  onSaved,
  onChanged,
  onError,
}: {
  row: Company | 'new' | null
  /** The company's active domain rows. */
  domains: CompanyEmailDomain[]
  onClose: () => void
  onSaved: (message: string) => void
  /** A domain was added or retired — the list behind the dialog is stale. */
  onChanged: () => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [companyId, setCompanyId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [newDomain, setNewDomain] = useState('')
  const [saving, setSaving] = useState(false)
  const [domainBusy, setDomainBusy] = useState(false)
  const [domainError, setDomainError] = useState<string | null>(null)

  useEffect(() => {
    if (!row) return
    setDomainError(null)
    setNewDomain('')
    if (row === 'new') {
      setCompanyId('')
      setCompanyName('')
      return
    }
    setCompanyId(row.company_id)
    setCompanyName(row.company_name)
  }, [row])

  const ready = companyId.trim() !== '' && companyName.trim() !== ''
  const renamed = !!existing && companyName.trim() !== existing.company_name

  async function addDomain() {
    const dom = normalizeDomain(newDomain)
    if (!dom || !existing) return
    setDomainBusy(true)
    setDomainError(null)
    try {
      await createCompanyEmailDomain(dom, existing.company_id)
      setNewDomain('')
      onChanged()
    } catch (e) {
      // A domain already mapped elsewhere comes back as a uniqueness violation.
      setDomainError(writeError(e, 'Could not add the domain'))
    } finally {
      setDomainBusy(false)
    }
  }

  async function retireDomain(d: CompanyEmailDomain) {
    setDomainBusy(true)
    setDomainError(null)
    try {
      await updateCompanyEmailDomain(d.id, { is_active: false, row_version: d.row_version })
      onChanged()
    } catch (e) {
      setDomainError(writeError(e, 'Could not remove the domain'))
    } finally {
      setDomainBusy(false)
    }
  }

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        const created = await createCompany({ company_id: companyId.trim(), company_name: companyName.trim() })
        const dom = normalizeDomain(newDomain)
        if (dom) await createCompanyEmailDomain(dom, created.company_id)
        onSaved(`Company added${dom ? ` — email domain ${dom}` : ''}`)
      } else if (existing) {
        if (renamed) {
          await updateCompany(existing.id, { company_name: companyName.trim(), row_version: existing.row_version })
          onSaved('Company renamed')
        } else {
          onSaved('No change')
        }
      }
    } catch (e) {
      onError(writeError(e, isNew ? 'Could not add company' : 'Could not rename company'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add company' : 'Company details'}</DialogTitle>
          <DialogContent>
            {/* The one thing a rename does NOT do: rewrite history. */}
            <Body1 className={s.dialogWarn}>
              A company can be renamed here. The name captured on an invoice when it was matched stays frozen as the
              accounting record — it is what the document was read as, and it is not re-synced. A rename changes the
              master and every match from now on.
            </Body1>
            <Field label="Company ID" required className={s.dialogField}>
              <Input
                value={companyId}
                onChange={(_, d) => setCompanyId(d.value)}
                readOnly={!isNew}
                appearance={isNew ? undefined : 'filled-darker'}
              />
            </Field>
            <Field label="Company name" required className={s.dialogField}>
              <Input value={companyName} onChange={(_, d) => setCompanyName(d.value)} />
            </Field>

            <Field
              label="Uploader email domains"
              className={s.dialogField}
              hint="An upload from one of these domains is filed to this company automatically. A company may have several; a domain may belong to only one company. The Senders tab lists every domain alongside who may email in."
            >
              <div>
                {existing && domains.length === 0 && (
                  <Caption1 className={s.mdHint}>
                    No domains yet — uploads from this company&rsquo;s staff will be filed by what the document says.
                  </Caption1>
                )}
                {existing &&
                  domains.map((d) => (
                    <div key={d.id} className={s.domainRow}>
                      <span className={s.domainName}>{d.domain}</span>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArchiveRegular />}
                        disabled={domainBusy}
                        onClick={() => void retireDomain(d)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                <div className={s.domainAdd}>
                  <Input
                    value={newDomain}
                    placeholder="@example.com"
                    onChange={(_, d) => setNewDomain(d.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && existing && newDomain.trim() && !domainBusy) void addDomain()
                    }}
                  />
                  {existing && (
                    <Button
                      icon={<AddRegular />}
                      disabled={domainBusy || !newDomain.trim()}
                      onClick={() => void addDomain()}
                    >
                      Add
                    </Button>
                  )}
                </div>
                {domainBusy && <Spinner size="tiny" />}
              </div>
            </Field>
            {domainError && <ErrorBanner message={domainError} />}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              {isNew ? 'Cancel' : 'Close'}
            </Button>
            <Button
              appearance="primary"
              disabled={saving || (isNew ? !ready : !renamed)}
              onClick={() => void save()}
            >
              {isNew ? 'Add company' : 'Save name'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- GL accounts -----------------------------------------------------------
const GL_CODE_RE = /^\d{6}-\d{2}-\d{3}$/

function AccountsManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Account[] | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<Account | 'new' | null>(null)
  const [term, setTerm] = useState('')
  // The retire target + the coding rules still pointing at either half of it.
  const [retire, setRetire] = useState<{ account: Account; defaults: VendorCodingRule[] } | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadAccountsForManagement())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (all ?? [])
      .filter((a) => showRetired || a.is_active)
      .filter((a) => !q || a.account_id.toLowerCase().includes(q) || a.account_name.toLowerCase().includes(q))
  }, [all, showRetired, term])

  // Before retiring, surface any coding rules still pointing at either HALF of
  // this GL code so they can be re-pointed or retired with it.
  //
  // A rule keyed on a half is NOT necessarily invalidated by retiring one
  // account — other accounts may still use that expense type — so these are
  // offered, never retired silently. Two things this cascade does NOT do, and
  // both are the server's: recomputing `is_active` on the two masters, and
  // refusing the retire while a non-terminal line still holds a half that would
  // go inactive. "Every scoping decision is a server decision."
  async function beginRetire(a: Account) {
    setBusyId(a.id)
    setError(null)
    try {
      const halves = { expense_type_code: a.account_id.slice(7), cost_center_code: a.account_id.slice(0, 6) }
      const [byExpenseType, byCostCenter] = await Promise.all([
        loadVendorCodingRules({ expense_type_code: halves.expense_type_code }),
        loadVendorCodingRules({ cost_center_code: halves.cost_center_code }),
      ])
      const seen = new Set<string>()
      const defaults = [...byExpenseType, ...byCostCenter].filter((r) => {
        if (seen.has(r.id)) return false
        seen.add(r.id)
        return r.is_active
      })
      setRetire({ account: a, defaults })
    } catch (e) {
      setError(`Could not check vendor coding rules: ${errorMessage(e)}`)
    } finally {
      setBusyId(null)
    }
  }

  async function confirmRetire(alsoDefaults: boolean) {
    if (!retire) return
    const a = retire.account
    setBusyId(a.id)
    setError(null)
    try {
      // THE ACCOUNT GOES FIRST, and the order is load-bearing. The server may
      // refuse this write — §4.5's in-flight guard returns 409 when retiring
      // this account would deactivate a half a non-terminal line still holds.
      // Retiring the rules first would leave them retired behind a refusal that
      // nothing rolls back, since each is its own request.
      await updateAccount(a.id, { is_active: false, row_version: a.row_version })
      if (alsoDefaults) {
        for (const d of retire.defaults) {
          await updateVendorCodingRule(d.id, { is_active: false, row_version: d.row_version })
        }
      }
      toast('GL account retired')
      setRetire(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not retire account'))
    } finally {
      setBusyId(null)
    }
  }

  async function reactivate(a: Account) {
    setBusyId(a.id)
    setError(null)
    try {
      await updateAccount(a.id, { is_active: true, row_version: a.row_version })
      toast('GL account reactivated')
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not reactivate account'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading GL accounts…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search code or name…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add account
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No accounts to show.' : 'No accounts match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((a) => (
            <MasterRow
              key={a.id}
              title={a.account_name || '(unnamed)'}
              subtitle={a.account_id}
              retired={!a.is_active}
              busy={busyId === a.id}
              onEdit={() => setEdit(a)}
              onToggleActive={() => (a.is_active ? void beginRetire(a) : void reactivate(a))}
            />
          ))}
        </div>
      )}
      <AccountDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'GL account added' : 'GL account updated')
          void load()
        }}
        onError={setError}
      />

      {/* Retire confirm — lists any vendor defaults still pointing at the code. */}
      <Dialog open={!!retire} onOpenChange={(_, d) => !d.open && setRetire(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Retire GL account?</DialogTitle>
            <DialogContent>
              {retire && (
                <>
                  <Body1>
                    Retire {retire.account.account_name || '(unnamed)'} ({retire.account.account_id})? It stops
                    matching new lines; you can reactivate it later.
                  </Body1>
                  {retire.defaults.length > 0 && (
                    <>
                      <Body1 style={{ marginTop: 12 }}>
                        {retire.defaults.length} coding rule{retire.defaults.length === 1 ? '' : 's'} name a half of
                        this account:
                      </Body1>
                      <ul className={s.retireList}>
                        {retire.defaults.map((d) => (
                          <li key={d.id}>
                            {d.vendor_id} → {[d.expense_type_code, d.cost_center_code].filter(Boolean).join(' / ')}
                          </li>
                        ))}
                      </ul>
                      <Caption1>
                        A rule naming a half may still be right — other accounts can use the same half. Re-point them
                        in Coding rules, or retire them along with the account.
                      </Caption1>
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setRetire(null)} disabled={!!busyId}>
                Cancel
              </Button>
              {retire && retire.defaults.length > 0 ? (
                <Button appearance="primary" onClick={() => void confirmRetire(true)} disabled={!!busyId}>
                  Retire account + {retire.defaults.length} default{retire.defaults.length === 1 ? '' : 's'}
                </Button>
              ) : (
                <Button appearance="primary" onClick={() => void confirmRetire(false)} disabled={!!busyId}>
                  Retire
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

function AccountDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: Account | 'new' | null
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [accountId, setAccountId] = useState('')
  const [accountName, setAccountName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setAccountId('')
      setAccountName('')
      return
    }
    setAccountId(row.account_id)
    setAccountName(row.account_name)
  }, [row])

  const codeValid = GL_CODE_RE.test(accountId.trim())
  const codeError = isNew && accountId.trim() !== '' && !codeValid
  // The id is read-only on edit, so only a new account is gated on the code shape.
  const ready = accountName.trim() !== '' && (isNew ? codeValid : true)

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        await createAccount({ account_id: accountId.trim(), account_name: accountName.trim() })
      } else if (existing) {
        await updateAccount(existing.id, { account_name: accountName.trim(), row_version: existing.row_version })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save GL account'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add GL account' : 'Edit GL account'}</DialogTitle>
          <DialogContent>
            <Field
              label="GL code"
              required
              className={s.dialogField}
              validationState={codeError ? 'error' : 'none'}
              validationMessage={codeError ? 'Use the 6-2-3 format, e.g. 689109-67-520.' : undefined}
            >
              <Input
                value={accountId}
                onChange={(_, d) => setAccountId(d.value)}
                readOnly={!isNew}
                appearance={isNew ? undefined : 'filled-darker'}
                placeholder="NNNNNN-NN-NNN"
              />
            </Field>
            <Field label="Account name" required className={s.dialogField}>
              <Input value={accountName} onChange={(_, d) => setAccountName(d.value)} />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add GL account' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Vendor coding rules ----------------------------------------------------
//
// A rule says either half of an account, or both, optionally narrowed to lines
// whose description contains a phrase. Each one reads back as a full sentence,
// with no second line and no source annotation — staff are the only ones who
// ever add one, so naming the source on screen tells nobody anything:
//
//   When an invoice from GORDON FOOD SERVICE has any line, default the expense
//   type to Food & Staples and the cost center to Dietary.
//
// A blank scope reads "has any line" in its place, and a rule naming only one
// half drops the other clause. This fires per LINE ITEM during the pass-2
// matching run (backend/src/domain/matchingLadder.ts, backend/src/lambda/
// match.ts) — not "when an invoice is received", which is a different, false
// claim about the same rule.
//
// Two things a rule author must be told, because neither is guessable:
//
//  * A COST-CENTER-ONLY RULE applies only where it composes with whatever expense
//    type resolves. Plant Operations & Maintenance forms a real account with 29
//    of the 82 expense types; Housekeeping with 9. The dialog shows the count.
//  * THE SCOPE IS NORMALIZED — lowercased, digits stripped, whitespace collapsed,
//    the same normalization GL precedent uses. So "2% milk" matches on "milk" and
//    a scope of "2%" would match nothing. The dialog shows the normalized form.
//
// WHAT MAKES A RULE FIRE is a scope narrow enough to be the only rule at its
// level for the half it names. The 37 migrated rows all sit at the blank scope
// with several accounts per vendor, so none of them fires as migrated — that is
// the pre-launch review, not a defect.
//
// The table stores codes only, so the names come from the masters: columns of
// digits are unreadable. Both halves are small static lists and are fetched
// whole by the memoized label loaders. The vendor master is not (6,210 rows), and
// there is no read-a-vendor-by-id endpoint, so `loadVendorNames` resolves only
// the ids the loaded rules name — bounded, and best-effort by design. A vendor
// whose name did not resolve shows its id, which is what matching keys on anyway.

/** The scope normalization, mirrored from `normalizeDescription` for preview. */
function normalizeScope(value: string): string {
  return value
    .toLowerCase()
    .replace(/[.,\-/&#]/g, ' ')
    .replace(/[0-9]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function VendorDefaultsManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<VendorCodingRule[] | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<VendorCodingRule | 'new' | null>(null)
  const [confirm, setConfirm] = useState<VendorCodingRule | null>(null)
  const [term, setTerm] = useState('')
  const [vendorNames, setVendorNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [expenseTypeNames, setExpenseTypeNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [costCenterNames, setCostCenterNames] = useState<ReadonlyMap<string, string>>(new Map())

  const load = useCallback(async () => {
    setError(null)
    try {
      // The two halves come from the LABEL loaders, not the pickers: they are
      // memoized, they ask for the whole list, and they carry no `active`
      // filter. A rule may name a half retired under it — that rule still has
      // to read back as a sentence rather than as digits.
      const [rules, expenseTypes, costCenters] = await Promise.all([
        loadVendorCodingRules(),
        loadExpenseTypeLabels().catch(() => new Map<string, string>()),
        loadCostCenterLabels().catch(() => new Map<string, string>()),
      ])
      setAll(rules)
      setExpenseTypeNames(expenseTypes)
      setCostCenterNames(costCenters)
      // Vendor names for the ids these rules actually name, bounded and
      // best-effort — `loadVendorNames` owns the reasons.
      setVendorNames(await loadVendorNames(rules.map((r) => r.vendor_id)))
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const vendorName = useCallback((code: string) => vendorNames.get(code) ?? '', [vendorNames])
  const expenseTypeName = useCallback(
    (code?: string | null) => (code ? expenseTypeNames.get(code) ?? code : ''),
    [expenseTypeNames],
  )
  const costCenterName = useCallback(
    (code?: string | null) => (code ? costCenterNames.get(code) ?? code : ''),
    [costCenterNames],
  )

  /** The rule as a full, true sentence — the whole point of the row. It fires
      per LINE ITEM during pass-2 matching, so the lead clause says "has a
      line", never "is received". There is no Account clause: a rule names
      either half, or both — never a whole account — and the CHECK constraint
      `vendor_coding_rule_says_something` bars neither being set, but the final
      fallback below covers that case anyway rather than emitting a dangling
      "default the". */
  const sentence = useCallback(
    (r: VendorCodingRule): string => {
      const who = vendorName(r.vendor_id) || r.vendor_id
      const scope = r.description_scope?.trim()
      const lead = scope
        ? `When an invoice from ${who} has a line mentioning "${scope}"`
        : `When an invoice from ${who} has any line`
      const et = r.expense_type_code ? expenseTypeName(r.expense_type_code) : null
      const cc = r.cost_center_code ? costCenterName(r.cost_center_code) : null
      if (et && cc) return `${lead}, default the expense type to ${et} and the cost center to ${cc}.`
      if (et) return `${lead}, default the expense type to ${et}.`
      if (cc) return `${lead}, default the cost center to ${cc}.`
      return `${lead}.`
    },
    [vendorName, expenseTypeName, costCenterName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    // Search the STRUCTURED fields, not the sentence: the sentence's own
    // template words ("expense type", "cost center", "invoice", "line",
    // "default", "any") would otherwise match every row.
    const haystack = (d: VendorCodingRule) =>
      [
        d.vendor_id,
        vendorName(d.vendor_id),
        d.description_scope ?? '',
        d.expense_type_code ?? '',
        d.cost_center_code ?? '',
        expenseTypeName(d.expense_type_code),
        costCenterName(d.cost_center_code),
      ]
        .join(' ')
        .toLowerCase()
    return (all ?? [])
      .filter((d) => showRetired || d.is_active)
      .filter((d) => !q || haystack(d).includes(q))
  }, [all, showRetired, term, vendorName, expenseTypeName, costCenterName])

  async function toggleActive(d: VendorCodingRule) {
    setBusyId(d.id)
    setError(null)
    try {
      await updateVendorCodingRule(d.id, { is_active: !d.is_active, row_version: d.row_version })
      toast(d.is_active ? 'Default retired' : 'Default reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update default'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading defaults…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search vendor, expense type or cost center…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add default
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No defaults to show.' : 'No defaults match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((d) => (
            <MasterRow
              key={d.id}
              title={sentence(d)}
              wrapTitle
              retired={!d.is_active}
              busy={busyId === d.id}
              onEdit={() => setEdit(d)}
              onToggleActive={() => (d.is_active ? setConfirm(d) : void toggleActive(d))}
            />
          ))}
        </div>
      )}
      <VendorDefaultDialog
        row={edit}
        vendorName={vendorName}
        expenseTypeName={expenseTypeName}
        costCenterName={costCenterName}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Default added' : 'Default updated')
          void load()
        }}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire default?"
        body={confirm ? `Retire this default? ${sentence(confirm)} You can reactivate it later.` : ''}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function VendorDefaultDialog({
  row,
  vendorName,
  expenseTypeName,
  costCenterName,
  onClose,
  onSaved,
  onError,
}: {
  row: VendorCodingRule | 'new' | null
  /** Resolvers from `VendorDefaultsManager`'s label loaders — used only to seed
      an EDIT with labels instead of raw codes; a fresh pick already carries its
      own label via `EntityPicker`'s `onSelect`. */
  vendorName: (code: string) => string
  expenseTypeName: (code?: string | null) => string
  costCenterName: (code?: string | null) => string
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [expenseType, setExpenseType] = useState<{ id?: string; name?: string }>({})
  const [costCenter, setCostCenter] = useState<{ id?: string; name?: string }>({})
  const [scope, setScope] = useState('')
  const [saving, setSaving] = useState(false)
  /** How constrained a cost-center-only rule is: it applies only where it composes. */
  const [costCenterReach, setCostCenterReach] = useState<{ fits: number; total: number } | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setVendor({})
      setExpenseType({})
      setCostCenter({})
      setScope('')
      return
    }
    // Seed with LABELS, not codes — an edit used to open showing "67-502"
    // rather than the account's actual name.
    setVendor({ id: row.vendor_id, name: vendorName(row.vendor_id) || row.vendor_id })
    setExpenseType({
      id: row.expense_type_code ?? undefined,
      name: row.expense_type_code ? expenseTypeName(row.expense_type_code) : undefined,
    })
    setCostCenter({
      id: row.cost_center_code ?? undefined,
      name: row.cost_center_code ? costCenterName(row.cost_center_code) : undefined,
    })
    setScope(row.description_scope ?? '')
    // `row` is the only thing this effect seeds from — the label resolvers are
    // memoized callbacks from the manager and stable across a render, and
    // listing them here would not change when they fire, only add churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  // The reach warning is only meaningful on a cost-center-ONLY rule: with an
  // expense type set the pair is fixed and the server refuses a non-composing
  // one. Computed over the WHOLE chart (`loadCostCenterReach`), not one page —
  // see that function's docstring for the defect a page-bounded count caused.
  useEffect(() => {
    if (!costCenter.id || expenseType.id) {
      setCostCenterReach(null)
      return
    }
    let cancelled = false
    loadCostCenterReach(costCenter.id)
      .then((reach) => {
        if (!cancelled) setCostCenterReach(reach)
      })
      .catch(() => !cancelled && setCostCenterReach(null))
    return () => {
      cancelled = true
    }
  }, [costCenter.id, expenseType.id])

  const normalizedScope = normalizeScope(scope)
  const scopeChanged = scope.trim() !== '' && normalizedScope !== scope.trim().toLowerCase()
  const scopeEmptied = scope.trim() !== '' && normalizedScope === ''
  // A rule must say something. Both halves set is validated by the SERVER, which
  // refuses a pair that is not a real account — a pre-flight check the server
  // then re-checks is a race, and this screen does not keep the chart.
  const ready = !!vendor.id && (!!expenseType.id || !!costCenter.id)

  async function save() {
    if (!ready || !vendor.id) return
    setSaving(true)
    try {
      const body = {
        description_scope: normalizedScope === '' ? null : normalizedScope,
        expense_type_code: expenseType.id ?? null,
        cost_center_code: costCenter.id ?? null,
      }
      if (isNew) {
        await createVendorCodingRule({ vendor_id: vendor.id, ...body })
      } else if (existing) {
        await updateVendorCodingRule(existing.id, { ...body, row_version: existing.row_version })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save default'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add default' : 'Edit default'}</DialogTitle>
          <DialogContent>
            <Field label="Vendor" className={s.dialogField}>
              {isNew ? (
                <EntityPicker
                  value={vendor}
                  placeholder="Search vendors…"
                  search={(t) =>
                    searchVendors(t).then<EntityOption[]>((vs) =>
                      vs.map((v) => ({
                        id: v.vendor_id,
                        primary: v.vendor_name || '(unnamed)',
                        secondary: [v.city, v.state_or_province].filter(Boolean).join(', ') || undefined,
                      })),
                    )
                  }
                  onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
                />
              ) : (
                <Input value={existing?.vendor_id ?? ''} readOnly appearance="filled-darker" />
              )}
            </Field>
            <Field
              label="Only lines containing"
              hint="Leave blank to apply to every line from this vendor."
              className={s.dialogField}
            >
              <Input value={scope} onChange={(_, d) => setScope(d.value)} placeholder="e.g. shirt" />
            </Field>
            {scopeEmptied ? (
              <Body1 className={s.dialogWarn}>
                That text normalizes to nothing, so the rule would apply to every line from this
                vendor. Line descriptions are matched with digits removed and punctuation collapsed.
              </Body1>
            ) : scopeChanged ? (
              <Body1 className={s.dialogWarn}>
                Matched as &ldquo;{normalizedScope}&rdquo; — line descriptions are compared with
                digits removed and punctuation collapsed.
              </Body1>
            ) : null}
            <Field label="What is it? (Expense Type)" className={s.dialogField}>
              <div className={s.pickerRow}>
                <EntityPicker
                  value={expenseType}
                  placeholder="Search Expense Types…"
                  search={(t) =>
                    searchExpenseTypes(t).then<EntityOption[]>((es) =>
                      es.map((e) => ({ id: e.expense_type_code, primary: e.label, secondary: e.expense_type_code })),
                    )
                  }
                  onSelect={(o) => {
                    setExpenseType({ id: o.id, name: o.primary })
                    // Changing the Expense Type invalidates whatever Cost Center
                    // was chosen against the OLD one — the same reset the invoice
                    // modals apply (owner ruling 2026-08-10). This runs only from
                    // an actual pick, never from an effect keyed on
                    // `expenseType.id`: an effect would also fire when the
                    // seeding effect above sets both halves for an EXISTING
                    // default, wiping the saved Cost Center of every row merely
                    // opened for edit.
                    setCostCenter({})
                  }}
                />
                {expenseType.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Expense Type"
                    onClick={() => {
                      setExpenseType({})
                      setCostCenter({})
                    }}
                  />
                )}
              </div>
            </Field>
            <Field label="Who is it for? (Cost Center)" className={s.dialogField}>
              <div className={s.pickerRow}>
                <EntityPicker
                  value={costCenter}
                  placeholder="Search Cost Centers…"
                  optionsKey={expenseType.id ?? ''}
                  search={(t) =>
                    searchCostCenters(t, expenseType.id).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.cost_center_code, primary: c.label, secondary: c.cost_center_code })),
                    )
                  }
                  onSelect={(o) => setCostCenter({ id: o.id, name: o.primary })}
                />
                {costCenter.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Cost Center"
                    onClick={() => setCostCenter({})}
                  />
                )}
              </div>
            </Field>
            {costCenterReach !== null && (
              <Body1 className={s.dialogWarn}>
                A rule with only a Cost Center applies only where it composes a real account with
                the Expense Type that resolves. {costCenter.name ?? costCenter.id} composes with{' '}
                {costCenterReach.fits} of the {costCenterReach.total} Expense Types; on every other
                line the rule contributes nothing.
              </Body1>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add default' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Scanners ----------------------------------------------------------------
// Scanner From-display-name -> company: the FALLBACK company for documents a
// facility scanner emails in. All the scanners send from one shared relay
// address, so the display name — "scanner2" in `scanner2 <scanners@pas-lp.com>`
// — is the only signal saying which facility fed the paper in. NOT
// authoritative: matching reads the document first, and this mapping applies
// only when the document itself resolves no company.

function ScannersManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<CompanyScanner[] | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<CompanyScanner | 'new' | null>(null)
  const [confirm, setConfirm] = useState<CompanyScanner | null>(null)
  const [term, setTerm] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadCompanyScanners())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (all ?? [])
      .filter((d) => showRetired || d.is_active)
      .filter(
        (d) =>
          !q ||
          d.scanner_name.includes(q) ||
          (d.company_name ?? '').toLowerCase().includes(q) ||
          d.company_id.toLowerCase().includes(q),
      )
  }, [all, showRetired, term])

  async function toggleActive(d: CompanyScanner) {
    setBusyId(d.id)
    setError(null)
    try {
      await updateCompanyScanner(d.id, { is_active: !d.is_active, row_version: d.row_version })
      toast(d.is_active ? 'Scanner retired' : 'Scanner reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update scanner'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading scanners…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <Body1 className={s.mdHint}>
        A scanner is matched by the name on its emails — the “scanner2” in “scanner2
        &lt;scanners@…&gt;”. Documents it sends are filed to its company only when the document
        itself doesn’t say whose it is.
      </Body1>
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search scanner or company…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add scanner
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No scanners to show.' : 'No scanners match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((d) => (
            <MasterRow
              key={d.id}
              title={`${d.scanner_name} → ${d.company_name ?? d.company_id}`}
              subtitle={d.company_id}
              retired={!d.is_active}
              busy={busyId === d.id}
              onEdit={() => setEdit(d)}
              onToggleActive={() => (d.is_active ? setConfirm(d) : void toggleActive(d))}
            />
          ))}
        </div>
      )}
      <ScannerDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Scanner added' : 'Scanner updated')
          void load()
        }}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire scanner?"
        body={`Retire ${confirm?.scanner_name ?? ''}? Its documents will no longer default to ${
          confirm?.company_name ?? confirm?.company_id ?? ''
        }. You can reactivate it later.`}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function ScannerDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: CompanyScanner | 'new' | null
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [name, setName] = useState('')
  const [company, setCompany] = useState<{ id?: string; name?: string }>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setName('')
      setCompany({})
      return
    }
    setName(row.scanner_name)
    setCompany({ id: row.company_id, name: row.company_name ?? row.company_id })
  }, [row])

  const normalized = normalizeScannerName(name)
  const nameChanged = name.trim() !== '' && normalized !== name.trim()
  const ready = (isNew ? normalized !== '' : true) && !!company.id

  async function save() {
    if (!ready || !company.id) return
    setSaving(true)
    try {
      if (isNew) {
        await createCompanyScanner(normalized, company.id)
      } else if (existing) {
        // The NAME is the identity of the row (the match key), so an edit
        // repoints the company only. Renaming a device is retire + add.
        await updateCompanyScanner(existing.id, {
          company_id: company.id,
          row_version: existing.row_version,
        })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save scanner'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add scanner' : 'Edit scanner'}</DialogTitle>
          <DialogContent>
            <Field
              label="Scanner name"
              hint="The name on the scanner’s emails, before the address. Stored lowercase."
              className={s.dialogField}
            >
              {isNew ? (
                <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="e.g. scanner2" />
              ) : (
                <Input value={existing?.scanner_name ?? ''} readOnly appearance="filled-darker" />
              )}
            </Field>
            {isNew && nameChanged && (
              <Body1 className={s.dialogWarn}>Will be stored as “{normalized}”.</Body1>
            )}
            <Field label="Company" className={s.dialogField}>
              <EntityPicker
                value={company}
                placeholder="Search companies…"
                search={(t) =>
                  loadCompaniesForManagement(t).then<EntityOption[]>((cs) =>
                    cs
                      .filter((c) => c.is_active !== false)
                      .map((c) => ({ id: c.company_id, primary: c.company_name, secondary: c.company_id })),
                  )
                }
                onSelect={(o) => setCompany({ id: o.id, name: o.primary })}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add scanner' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Senders -------------------------------------------------------------------
// This tab used to say "may email documents into intake" and show no routing
// at all, which was wrong on two counts: the uploader-domain rung
// (`company_email_domains`) already governs IN-APP uploads as well as email —
// `uploads.ts` sets `source` from the signed-in principal for a non-service
// caller, `extract.ts` copies it to `invoices.uploader_email`, and
// `matchingLadder.ts` matches that address's domain against this same table —
// and the routing table was invisible here even for email. So this tab now
// shows BOTH facts side by side, per entry: `intake_senders` (may this
// address email documents in) and `company_email_domains` (which company a
// domain's uploads and emails file to). They stay two tables and two writes —
// `mergeSenderEntries` (`../lib/senders`) only joins them for display, on the
// exact stored string. Editing a company's domains from Companies → that
// company still works; this is the second door onto the same two masters.

/** Trim + lowercase, and prepend a leading `@` when the typed text has no `@`
    at all — so a bare domain like `pas-lp.com` is stored as `@pas-lp.com`,
    the same convention `normalizeDomain` uses for the Companies tab's domain
    editor. An address the caller already typed with an `@` is left exactly as
    typed (just trimmed and lowercased) — never widened into a domain. */
function normalizeSenderKey(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (t === '' || t.includes('@')) return t
  return `@${t}`
}

/** One row: the key, both facts as badges, and a two-line subtitle spelling
    out what each fact means for THIS entry — not `MasterRow`, which carries
    one Active/Retired badge and one Retire button and cannot express two
    independent lifecycles. */
function SenderRow({
  entry,
  companyNames,
  onEdit,
}: {
  entry: SenderEntry
  companyNames: ReadonlyMap<string, string>
  onEdit: () => void
}) {
  const s = useStyles()
  const intakeClause = entry.intake
    ? entry.isDomain
      ? 'Any sender at this domain may email documents into intake'
      : 'May email documents into intake'
    : entry.intakeHistory.length > 0
      ? 'Mail from this entry is skipped'
      : 'Not on the intake list'
  const resolvedName = routedCompanyName(entry, companyNames)
  const routingClause = !entry.isDomain
    ? null
    : entry.routing
      ? `Uploads and emails from this domain file to ${resolvedName ?? entry.routing.company_id}`
      : 'No company routing — the document decides'
  const subtitle = [intakeClause, routingClause].filter((c): c is string => !!c).join('  ·  ')
  return (
    <div className={mergeClasses(s.mdRow, !entryIsActive(entry) && s.mdRetired)}>
      <div className={s.mdRowMain}>
        <span className={s.mdRowTitle} title={entry.key}>
          {entry.key}
        </span>
        <span className={s.mdRowSub}>{subtitle}</span>
      </div>
      <div className={s.senderBadges}>
        <Badge appearance="outline" color={entry.intake ? 'success' : 'subtle'}>
          {entry.intake ? 'Intake allowed' : 'Intake not allowed'}
        </Badge>
        {entry.isDomain && (
          <Badge appearance="outline" color={entry.routing ? 'brand' : 'subtle'}>
            {entry.routing ? `Routes to ${resolvedName ?? entry.routing.company_id}` : 'No routing'}
          </Badge>
        )}
      </div>
      <div className={s.mdRowActions}>
        <Button appearance="subtle" size="small" icon={<EditRegular />} aria-label="Edit" onClick={onEdit} />
      </div>
    </div>
  )
}

function SendersManager() {
  const s = useStyles()
  const toast = useToast()
  const [entries, setEntries] = useState<SenderEntry[] | null>(null)
  const [companyNames, setCompanyNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [edit, setEdit] = useState<SenderEntry | 'new' | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [senders, domains, companies] = await Promise.all([
        loadIntakeSenders(),
        loadCompanyEmailDomains(),
        loadCompaniesForManagement(),
      ])
      setEntries(mergeSenderEntries(senders, domains))
      setCompanyNames(new Map(companies.map((c) => [c.company_id, c.company_name])))
    } catch (e) {
      setError(errorMessage(e))
      setEntries([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (entries ?? [])
      .filter((e) => showRetired || entryIsActive(e))
      .filter((e) => {
        if (!q) return true
        if (e.key.toLowerCase().includes(q)) return true
        const name = routedCompanyName(e, companyNames)
        return !!name && name.toLowerCase().includes(q)
      })
  }, [entries, showRetired, term, companyNames])

  if (entries === null) return <Spinner label="Loading senders…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <Body1 className={s.mdIntro}>
        Two facts live on each entry. <strong>Intake</strong> says who may email documents to the intake
        mailbox — every accepted page is a billed AI read. <strong>Routing</strong> says which company a
        document files to, and it applies to <strong>in-app uploads as well as email</strong>, because both
        record the uploader&rsquo;s own address. A domain&rsquo;s routing beats what the document says.
      </Body1>
      <Body1 className={s.mdIntro}>Only a whole domain can carry routing; a single address cannot.</Body1>
      <Body1 className={s.mdIntro}>
        Senders pinned in configuration are a bootstrap floor and are not listed here, and this list
        governs the SES intake path — the Graph mailbox path has no database and reads the pinned floor
        alone.
      </Body1>
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search address, domain or company…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add sender or routing rule
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(entries ?? []).length === 0 ? 'No senders to show.' : 'No senders match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((e) => (
            <SenderRow key={e.key} entry={e} companyNames={companyNames} onEdit={() => setEdit(e)} />
          ))}
        </div>
      )}
      <SenderDialog
        row={edit}
        companyNames={companyNames}
        onClose={() => setEdit(null)}
        onSaved={(message, isError) => {
          setEdit(null)
          toast(message, isError ? 'error' : 'success')
          void load()
        }}
        onError={setError}
      />
    </div>
  )
}

function SenderDialog({
  row,
  companyNames,
  onClose,
  onSaved,
  onError,
}: {
  row: SenderEntry | 'new' | null
  companyNames: ReadonlyMap<string, string>
  onClose: () => void
  /** Full success, OR a partial write (`isError` true) that already changed
      something — either way the caller closes, toasts and reloads, because
      "always reload" is the only way the screen can show what actually
      landed against two masters with no shared transaction. */
  onSaved: (message: string, isError?: boolean) => void
  /** Nothing landed at all — the dialog stays open so the mistake can be fixed. */
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [key, setKey] = useState('')
  const [intakeOn, setIntakeOn] = useState(false)
  const [company, setCompany] = useState<{ id?: string; name?: string }>({})
  const [saving, setSaving] = useState(false)
  const [confirmOffs, setConfirmOffs] = useState<string[] | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setKey('')
      setIntakeOn(false)
      setCompany({})
      return
    }
    setKey(row.key)
    setIntakeOn(row.intake !== null)
    setCompany(
      row.routing
        ? { id: row.routing.company_id, name: routedCompanyName(row, companyNames) ?? row.routing.company_id }
        : {},
    )
    // Seeded from `row` alone — `companyNames` is read once at seed time to
    // resolve the label; a later re-render of the map (e.g. after a reload
    // elsewhere) should not retroactively rewrite what the dialog is showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  const normalizedKey = isNew ? normalizeSenderKey(key) : existing?.key ?? ''
  const isDomain = normalizedKey.startsWith('@')
  const keyChanged = isNew && key.trim() !== '' && normalizedKey !== key.trim().toLowerCase()
  const ready = isNew ? normalizedKey !== '' && (intakeOn || !!company.id) : true

  // A picked company is routing state, and routing only applies to a domain
  // key. If the user picks a company and then edits the key so it stops being
  // a domain (`jane@x.test` after `@x.test`), the picker unmounts but
  // `company` state survives — leaving `ready` true via a stale `company.id`
  // that `doSave`'s `if (isDomain)` branch never reads, so the primary button
  // would write nothing (no intake row when `intakeOn` is off, no routing
  // write at all) and still report success. Drop the stale state the moment
  // `isDomain` goes false so a re-add starts clean rather than carrying a
  // hidden company id back in if the user retypes the `@`.
  useEffect(() => {
    if (!isDomain) setCompany({})
  }, [isDomain])

  /** What this save would stop, so a turn-off is confirmed rather than silent
      — the same confirmation every other manager's row-level Retire button
      already provides. Empty when nothing is being turned off. */
  function turnOffMessages(): string[] {
    const msgs: string[] = []
    if (existing?.intake && !intakeOn) {
      msgs.push(`Stop accepting emailed documents from ${existing.key}?`)
    }
    if (existing?.routing && !company.id) {
      const name = routedCompanyName(existing, companyNames) ?? existing.routing.company_id
      msgs.push(`Stop filing documents from ${existing.key} to ${name}?`)
    }
    return msgs
  }

  async function doSave() {
    setSaving(true)
    // Intake first — it has no foreign key, so it cannot fail on a bad company.
    try {
      if (intakeOn && !existing?.intake) {
        await createIntakeSender(normalizedKey)
      } else if (!intakeOn && existing?.intake) {
        await updateIntakeSender(existing.intake.id, { is_active: false, row_version: existing.intake.row_version })
      }
    } catch (e) {
      setSaving(false)
      onError(writeError(e, 'Could not save the intake setting'))
      return
    }
    if (isDomain) {
      try {
        if (company.id && !existing?.routing) {
          await createCompanyEmailDomain(normalizedKey, company.id)
        } else if (company.id && existing?.routing && company.id !== existing.routing.company_id) {
          await updateCompanyEmailDomain(existing.routing.id, {
            company_id: company.id,
            row_version: existing.routing.row_version,
          })
        } else if (!company.id && existing?.routing) {
          await updateCompanyEmailDomain(existing.routing.id, {
            is_active: false,
            row_version: existing.routing.row_version,
          })
        }
      } catch (e) {
        // The intake half above already landed — say so rather than reporting
        // a clean failure, and reload so the screen shows what actually happened.
        setSaving(false)
        onSaved(writeError(e, 'The intake setting saved, but the routing did not'), true)
        return
      }
    }
    setSaving(false)
    onSaved(isNew ? 'Sender added' : 'Sender updated')
  }

  function attemptSave() {
    const offs = turnOffMessages()
    if (offs.length > 0) {
      setConfirmOffs(offs)
      return
    }
    void doSave()
  }

  return (
    <>
      <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{isNew ? 'Add sender or routing rule' : 'Edit sender'}</DialogTitle>
            <DialogContent>
              <Field
                label="Email address or domain"
                hint="name@example.com allows one sender to email in; @example.com covers a whole domain and can also carry company routing. Stored lowercase. The string is the identity of both facts, so it cannot be edited later — changing it is retire and re-add."
                className={s.dialogField}
              >
                {isNew ? (
                  <Input
                    value={key}
                    onChange={(_, d) => setKey(d.value)}
                    placeholder="e.g. @seniorlifejohnstown.com or scanners@pas-lp.com"
                  />
                ) : (
                  <Input value={existing?.key ?? ''} readOnly appearance="filled-darker" />
                )}
              </Field>
              {keyChanged && <Body1 className={s.dialogWarn}>Will be stored as &ldquo;{normalizedKey}&rdquo;.</Body1>}
              <div className={s.dialogField}>
                <Switch
                  label="May email documents into intake"
                  checked={intakeOn}
                  onChange={(_, d) => setIntakeOn(d.checked)}
                />
              </div>
              {isDomain ? (
                <Field label="File documents from this domain to" className={s.dialogField}>
                  <div className={s.pickerRow}>
                    <EntityPicker
                      value={company}
                      placeholder="Search companies…"
                      search={(t) =>
                        loadCompaniesForManagement(t).then<EntityOption[]>((cs) =>
                          cs
                            .filter((c) => c.is_active !== false)
                            .map((c) => ({ id: c.company_id, primary: c.company_name, secondary: c.company_id })),
                        )
                      }
                      onSelect={(o) => setCompany({ id: o.id, name: o.primary })}
                    />
                    {company.id && (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        onClick={() => setCompany({})}
                      >
                        Clear routing
                      </Button>
                    )}
                  </div>
                </Field>
              ) : (
                normalizedKey !== '' && (
                  <Caption1 className={s.mdHint}>
                    Company routing is per domain — a single address cannot carry one.
                  </Caption1>
                )
              )}
              {isDomain && company.id && (
                <Body1 className={s.dialogWarn}>
                  Map a domain only where every document from it belongs to this one company. The
                  organization&rsquo;s own domain, shared-staff sites, vendor domains and the scanner relay
                  are deliberately unmapped so their documents are filed by what the document says.
                </Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" disabled={!ready || saving} onClick={attemptSave}>
                {isNew ? 'Add sender or routing rule' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <ConfirmDialog
        open={confirmOffs !== null}
        title="Confirm change"
        body={(confirmOffs ?? []).join(' ')}
        confirmLabel="Continue"
        busy={saving}
        onConfirm={() => {
          setConfirmOffs(null)
          void doSave()
        }}
        onCancel={() => setConfirmOffs(null)}
      />
    </>
  )
}

// ---- Vehicles (the fleet master) -------------------------------------------
// The fleet is what vehicle tagging resolves against: a printed token is matched
// against Number / VIN / License Plate and the vehicle NUMBER is stored as the
// tag, so the number is the identifier that matters — required, and unique among
// active rows (the database enforces that; a collision comes back as
// `duplicate_key`).
//
// Status is the fleet owner's own vocabulary, stored verbatim and independent of
// our retired flag: a `Sold` vehicle stays an active record and stays taggable,
// because a final invoice for a sold vehicle still has to be coded.
function VehiclesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<Vehicle[] | null>(null)
  const [term, setTerm] = useState('')
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<Vehicle | 'new' | null>(null)
  const [confirm, setConfirm] = useState<Vehicle | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadVehiclesForManagement())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const t = term.trim().toLowerCase()
    return (all ?? [])
      .filter((v) => showRetired || v.is_active)
      .filter(
        (v) =>
          !t ||
          v.number.toLowerCase().includes(t) ||
          (v.vin ?? '').toLowerCase().includes(t) ||
          (v.license_plate ?? '').toLowerCase().includes(t) ||
          (v.make ?? '').toLowerCase().includes(t) ||
          (v.model ?? '').toLowerCase().includes(t) ||
          (v.company_label ?? '').toLowerCase().includes(t) ||
          (v.department ?? '').toLowerCase().includes(t) ||
          (v.status ?? '').toLowerCase().includes(t),
      )
  }, [all, showRetired, term])

  async function toggleActive(v: Vehicle) {
    setBusyId(v.id)
    setError(null)
    try {
      await updateVehicle(v.id, { is_active: !v.is_active, row_version: v.row_version })
      toast(v.is_active ? 'Vehicle retired' : 'Vehicle reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update vehicle'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading fleet…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search number, VIN, plate, make, centre or status…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<ArrowUploadRegular />} onClick={() => setImportOpen(true)}>
          Import spreadsheet
        </Button>
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add vehicle
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>
          {(all ?? []).length === 0
            ? 'The fleet is empty. Until vehicles are added here, tagging a line with a vehicle has nothing to match and automatic vehicle resolution finds nothing.'
            : 'No vehicles match.'}
        </Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((v) => (
            <MasterRow
              key={v.id}
              title={[v.number ? `#${v.number}` : '(no number)', v.year, v.make, v.model].filter(Boolean).join(' ')}
              subtitle={[v.license_plate, v.vin, v.company_label, v.department, v.status].filter(Boolean).join('  ·  ')}
              retired={!v.is_active}
              busy={busyId === v.id}
              onEdit={() => setEdit(v)}
              onToggleActive={() => (v.is_active ? setConfirm(v) : void toggleActive(v))}
            />
          ))}
        </div>
      )}
      <VehicleDialog
        row={edit}
        known={all ?? []}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Vehicle added' : 'Vehicle updated')
          void load()
        }}
        onError={setError}
      />
      <FleetImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApplied={(summary) => {
          setImportOpen(false)
          toast(`Fleet imported — ${summary}`)
          void load()
        }}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire vehicle?"
        body={`Retire vehicle ${confirm?.number ?? ''}? It drops out of the tagging picker and out of automatic resolution, and existing tags are untouched. You can reactivate it later.`}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function VehicleDialog({
  row,
  known,
  onClose,
  onSaved,
  onError,
}: {
  row: Vehicle | 'new' | null
  /** Every vehicle we hold — the source of the Company / Department / Status
      suggestions, so hand entry converges on the values already in use. */
  known: Vehicle[]
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  // One field per column in the fleet owner's listing, in their order.
  const [num, setNum] = useState('')
  const [vin, setVin] = useState('')
  const [plate, setPlate] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [company, setCompany] = useState('')
  const [department, setDepartment] = useState('')
  const [status, setStatus] = useState('')
  const [saving, setSaving] = useState(false)
  // The dialog surface itself — same pattern as AssignmentsScreen's
  // `dialogSurface`, and simpler than resolving via `.closest` since this ref
  // IS the host: it's set directly on `DialogSurface` below, so there is no
  // ambiguity about which dialog it belongs to.
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  // Shared height/z-index for the freeform combobox listboxes below (Company /
  // Department / Status) — see `popupPositioning.ts`. The worst case for the
  // missing max-height: `company_label` alone can run to several dozen
  // distinct values across the fleet, uncapped and unscrolled without it.
  const popupListboxClass = usePopupListboxClass()

  useEffect(() => {
    if (!row) return
    const v = row === 'new' ? null : row
    setNum(v?.number ?? '')
    setVin(v?.vin ?? '')
    setPlate(v?.license_plate ?? '')
    setMake(v?.make ?? '')
    setModel(v?.model ?? '')
    setYear(v?.year ?? '')
    setCompany(v?.company_label ?? '')
    setDepartment(v?.department ?? '')
    setStatus(v?.status ?? '')
  }, [row])

  /** Distinct values already in the fleet, for the freeform suggestion lists.
      Seeded from the data rather than a hard-coded vocabulary, because the
      labels are the fleet owner's to invent. */
  const suggestions = useMemo(() => {
    const pick = (f: (v: Vehicle) => string | null | undefined) =>
      [...new Set(known.map((v) => (f(v) ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    return {
      company: pick((v) => v.company_label),
      department: pick((v) => v.department),
      status: pick((v) => v.status),
    }
  }, [known])

  // Only the number is required — it is the tag resolution stores. Everything
  // else mirrors their listing and may legitimately be blank.
  const ready = !!num.trim()

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      // `null` clears, so blanking a field here really blanks it — the same rule
      // the import follows for a blank cell in a column that is present.
      const orNull = (v: string) => (v.trim() === '' ? null : v.trim())
      const fields = {
        number: num.trim(),
        vin: orNull(vin),
        license_plate: orNull(plate),
        make: orNull(make),
        model: orNull(model),
        year: orNull(year),
        company_label: orNull(company),
        department: orNull(department),
        // Status is a plain string on the row, never null.
        status: status.trim(),
      }
      if (isNew) await createVehicle(fields)
      else if (existing) await updateVehicle(existing.id, { ...fields, row_version: existing.row_version })
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save vehicle'))
    } finally {
      setSaving(false)
    }
  }

  /** A freeform combobox: suggests what is already in use, accepts anything new —
      so a status or centre the fleet owner adds later needs no code change. */
  const freeform = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    options: string[],
    placeholder?: string,
  ) => (
    <Field label={label} className={s.dialogField}>
      <Combobox
        freeform
        value={value}
        selectedOptions={value ? [value] : []}
        placeholder={placeholder}
        // mountNode (dialogSurface) keeps the listbox inside the dialog's focus
        // trap — portaled to body it reads as "the dropdown closes as fast as it
        // opens" — and popupPositionProps also fixes autoSize collapsing it to
        // one scrolling row (app/README.md). Falls back to inlinePopup only if
        // dialogSurface hasn't mounted yet. `listbox` supplies the z-index and
        // max-height that `mountNode` + `autoSize: false` no longer provide on
        // their own.
        {...popupPositionProps(dialogSurface)}
        listbox={{ className: popupListboxClass }}
        onChange={(e) => setValue(e.target.value)}
        onOptionSelect={(_, d) => setValue(d.optionValue ?? '')}
      >
        {options.map((o) => (
          <Option key={o} value={o} text={o}>
            {o}
          </Option>
        ))}
      </Combobox>
    </Field>
  )

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add vehicle' : 'Edit vehicle'}</DialogTitle>
          <DialogContent>
            <div className={s.vehGrid}>
              <Field label="Veh #" className={s.dialogField} required>
                <Input value={num} onChange={(_, d) => setNum(d.value)} placeholder="e.g. 214" />
              </Field>
              <Field label="Year" className={s.dialogField}>
                <Input value={year} onChange={(_, d) => setYear(d.value)} placeholder="e.g. 2019" />
              </Field>
              <Field label="VIN" className={s.dialogField}>
                <Input value={vin} onChange={(_, d) => setVin(d.value)} />
              </Field>
              <Field label="License Plate" className={s.dialogField}>
                <Input value={plate} onChange={(_, d) => setPlate(d.value)} />
              </Field>
              <Field label="Make" className={s.dialogField}>
                <Input value={make} onChange={(_, d) => setMake(d.value)} placeholder="e.g. Ford" />
              </Field>
              <Field label="Model" className={s.dialogField}>
                <Input value={model} onChange={(_, d) => setModel(d.value)} placeholder="e.g. Transit" />
              </Field>
              {freeform('Company', company, setCompany, suggestions.company)}
              {freeform('Department', department, setDepartment, suggestions.department, 'operating centre')}
            </div>
            {freeform('Status', status, setStatus, suggestions.status)}
            <Caption1 className={s.mdHint}>
              These are the fleet owner’s own columns. A document is matched against the number, VIN and plate, but the{' '}
              <strong>Veh #</strong> is what gets stored on a tagged line. Company, Department and Status accept a new
              value if the fleet owner adds one.
            </Caption1>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add vehicle' : 'Save vehicle'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Fleet spreadsheet import ----------------------------------------------
// The workbook is parsed on the SERVER now. The browser posts the file, the API
// returns the plan it would apply — adds, per-field updates, unchanged, absent,
// duplicates, skipped rows, and how it read the header — and Apply commits that
// exact plan by its token. Preview and commit therefore cannot disagree about
// what the file said, and the client never interprets a spreadsheet (the old
// build read .xlsx bytes itself and wrote row by row with a progress bar).
//
// A record ABSENT from the file is reported and left alone: imports never
// retire, so a filtered or partial export cannot quietly retire half the fleet.
function FleetImportDialog({
  open,
  onClose,
  onApplied,
}: {
  open: boolean
  onClose: () => void
  onApplied: (summary: string) => void
}) {
  const s = useStyles()
  const [fileName, setFileName] = useState('')
  const [reading, setReading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<VehicleImportPreview | null>(null)
  const [applying, setApplying] = useState(false)
  const [dragging, setDragging] = useState(false)

  function reset() {
    setFileName('')
    setPreview(null)
    setError(null)
    setDragging(false)
  }

  async function onFile(file: File) {
    reset()
    setFileName(file.name)
    setReading(true)
    try {
      setPreview(await previewVehicleImport(file))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setReading(false)
    }
  }

  /** One call applies the whole plan. A preview token expires, and an expired one
      comes back as a conflict — the plan may no longer match the fleet, so the
      answer is to preview again rather than retry blindly. */
  async function apply() {
    if (!preview) return
    setApplying(true)
    setError(null)
    try {
      const result = await commitVehicleImport(preview.preview_token)
      const bits = [`${result.added} added`, `${result.updated} updated`]
      if (result.unchanged) bits.push(`${result.unchanged} unchanged`)
      if (result.absent_untouched) bits.push(`${result.absent_untouched} left alone`)
      onApplied(bits.join(', '))
      reset()
    } catch (e) {
      setError(
        isApiError(e) && e.status === 409
          ? `${e.message} Upload the file again to build a fresh preview.`
          : errorMessage(e),
      )
    } finally {
      setApplying(false)
    }
  }

  const writes = preview ? preview.adds.length + preview.updates.length : 0
  const mapping = preview?.mapping

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && (reset(), onClose())}>
      <DialogSurface className={s.importSurface}>
        <DialogBody>
          <DialogTitle>Import a fleet spreadsheet</DialogTitle>
          <DialogContent>
            {error && <ErrorBanner message={error} />}

            {!preview && (
              <div className={s.importIntro}>
                <Body1>
                  Upload the vehicle listing as it comes from the fleet owner — the columns are matched by name, so a
                  renamed or reordered export still works. Nothing is written until you review the changes.
                </Body1>
                {/* Drag-and-drop, with the file picker as the fallback — a
                    dropped .xlsx is the natural gesture for "here is the listing
                    the director just emailed me". */}
                <div
                  className={mergeClasses(s.dropZone, dragging && s.dropZoneActive)}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragging(true)
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setDragging(false)
                    const f = e.dataTransfer.files?.[0]
                    if (f) void onFile(f)
                  }}
                >
                  <ArrowUploadRegular fontSize={28} />
                  <Body1>Drop the spreadsheet here</Body1>
                  <Caption1 className={s.mdHint}>or</Caption1>
                  <input
                    type="file"
                    accept=".xlsx"
                    disabled={reading}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void onFile(f)
                    }}
                  />
                </div>
                {reading && <Spinner size="tiny" label={`Reading ${fileName}…`} />}
              </div>
            )}

            {preview && mapping && (
              <div className={s.importIntro}>
                <Caption1>
                  <strong>{fileName}</strong> · sheet “{preview.sheet_name}” · header row {preview.header_row}
                </Caption1>

                <div className={s.importCounts}>
                  <Badge appearance="filled" color="brand">{preview.adds.length} to add</Badge>
                  <Badge appearance="filled" color="success">{preview.updates.length} to update</Badge>
                  <Badge appearance="tint" color="informative">{preview.unchanged_count} unchanged</Badge>
                  {preview.absent.length > 0 && (
                    <Badge appearance="tint" color="warning">{preview.absent.length} not in this file</Badge>
                  )}
                </div>

                {/* Which of our fields the file did not carry. Those are LEFT
                    ALONE on every row it touches rather than blanked, so this is
                    a notice, not an error — but the user has to know. */}
                {mapping.missing.length > 0 && (
                  <Body1 className={s.dialogWarn}>
                    This file has no column for{' '}
                    <strong>{mapping.missing.map((f) => FLEET_FIELD_LABEL[f]).join(', ')}</strong>. Those values are
                    left exactly as they are on every row this import touches — nothing is blanked.
                  </Body1>
                )}
                {mapping.unmapped.length > 0 && (
                  <Body1 className={s.dialogWarn}>
                    These columns were not recognised and will be ignored: <strong>{mapping.unmapped.join(', ')}</strong>.
                    Everything else imports normally.
                  </Body1>
                )}
                {!mapping.deterministic && (
                  <Body1 className={s.dialogWarn}>
                    The column names in this file did not all match cleanly, so check the changes below carefully before
                    applying.
                  </Body1>
                )}
                {preview.duplicates.length > 0 && (
                  <Body1 className={s.dialogWarn}>
                    The file lists these vehicle numbers more than once:{' '}
                    <strong>{preview.duplicates.map((d) => `${d.number} (rows ${d.rows.join(', ')})`).join('; ')}</strong>.
                    The last row wins for each.
                  </Body1>
                )}
                {preview.skipped.length > 0 && (
                  <Body1 className={s.dialogWarn}>
                    {preview.skipped.length} {preview.skipped.length === 1 ? 'row was' : 'rows were'} skipped:{' '}
                    {preview.skipped
                      .slice(0, 8)
                      .map((r) => `row ${r.row} (${r.reason})`)
                      .join(', ')}
                    {preview.skipped.length > 8 ? ` … and ${preview.skipped.length - 8} more` : ''}.
                  </Body1>
                )}

                {/* What is in the fleet that the file does not mention. Left
                    untouched — this is a notice. */}
                {preview.absent.length > 0 && (
                  <div>
                    <Caption1 className={s.importSectionHead}>
                      In the fleet but not in this file — left unchanged
                    </Caption1>
                    <Body1 className={s.mdHint}>
                      {preview.absent
                        .slice(0, 40)
                        .map((v) => v.number)
                        .join(', ')}
                      {preview.absent.length > 40 ? ` … and ${preview.absent.length - 40} more` : ''}
                    </Body1>
                    <Caption1 className={s.mdHint}>
                      Nothing is retired by an import. If any of these have genuinely left the fleet, retire them
                      individually.
                    </Caption1>
                  </div>
                )}

                {preview.adds.length > 0 && (
                  <div>
                    <Caption1 className={s.importSectionHead}>New vehicles</Caption1>
                    <Body1 className={s.mdHint}>
                      {preview.adds
                        .slice(0, 40)
                        .map((a) => a.values.number ?? `row ${a.row}`)
                        .join(', ')}
                      {preview.adds.length > 40 ? ` … and ${preview.adds.length - 40} more` : ''}
                    </Body1>
                  </div>
                )}

                {preview.updates.length > 0 && (
                  <div>
                    <Caption1 className={s.importSectionHead}>Changes</Caption1>
                    <div className={s.importDiff}>
                      {preview.updates.slice(0, 60).map((u) => (
                        <div key={u.vehicle_id} className={s.importDiffRow}>
                          <span className={s.mdRowTitle}>#{u.number}</span>
                          <span>
                            {u.changes.map((c) => (
                              <Caption1 key={c.field} className={s.importChange}>
                                {FLEET_FIELD_LABEL[c.field]}: <s>{c.before || '(blank)'}</s> →{' '}
                                <strong>{c.after || '(blank)'}</strong>
                              </Caption1>
                            ))}
                          </span>
                        </div>
                      ))}
                      {preview.updates.length > 60 && (
                        <Caption1 className={s.mdHint}>… and {preview.updates.length - 60} more</Caption1>
                      )}
                    </div>
                  </div>
                )}

                <Caption1 className={s.mdHint}>
                  Columns read: {FLEET_FIELDS.filter((f) => mapping.by_field[f] !== undefined)
                    .map((f: FleetField) => FLEET_FIELD_LABEL[f])
                    .join(', ') || 'none'}
                  .
                </Caption1>

                {applying && <Spinner size="tiny" label="Applying…" />}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => { reset(); onClose() }} disabled={applying}>
              Cancel
            </Button>
            {preview && (
              <Button appearance="primary" disabled={applying || writes === 0} onClick={() => void apply()}>
                {writes === 0 ? 'Nothing to write' : `Apply ${writes} ${writes === 1 ? 'change' : 'changes'}`}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
